import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Button, Grid, Paper, TableContainer, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import * as api from '../../api/app/dashboardsApi';
import { AlertDialog } from '../../components/core/AlertDialog';
import { ApiError } from '../../components/core/ApiError/ApiError';
import DataGridTable from '../../components/core/DataGridTable/DataGridTable';
import { Loader } from '../../components/core/Loader/Loader';
import { useTranslation } from '../../contexts/translation';
import { useDeleteDialog } from '../../hooks/useDeleteDialog';
import { Dashboard, DashboardType, dashboardTypes } from '../../types/app/dashboards.types';
import { dictionary } from '../../utils/core/dictionary';
import { paths } from '../../utils/core/routes';
interface Props {
  parentId: number;
  isReadOnly: boolean;
}
export function DashboardList({ parentId, isReadOnly }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const {
    isOpen: isDeleteDialogOpen,
    idToDelete,
    setDeleteAttempt,
    cancelDeleteAttempt,
    confirmDeleteAttempt,
  } = useDeleteDialog();

  const {
    data: dashboardList,
    isLoading,
    isError,
  } = useQuery<Dashboard[]>(['dashboards', parentId], () => api.getDashboardList(parentId));
  const { mutate: mutateDeleteDashboard } = useMutation(api.deleteDashboard);

  const columns = [
    { headerName: 'Name', field: 'name', minWidth: 200 },
    {
      headerName: 'Type',
      field: 'type',
      minWidth: 150,
      renderCell: (t: any) =>
        dashboardTypes.map((type: DashboardType) => {
          if (type.code === t.row.type) return type.name;
        }),
    },
    {
      headerName: 'URL',
      field: 'url',
      minWidth: 650,
      flex: 1,
      renderCell: (fields: any) => {
        if (fields && !fields.row.url) return;

        return (
          <Link
            key={fields.row.id}
            to={''}
            onClick={() => window.open(fields.row.url, 'toolbar=no,resizable=yes,location=yes')}
            style={{ color: theme.palette.primary.main, fontWeight: 600 }}
          >
            {fields.row.url}
          </Link>
        );
      },
    },
    {
      field: '',
      sortable: false,
      disableColumnMenu: true,
      flex: 1,
      minWidth: 150,
      align: 'right',
      hide: isReadOnly,
      renderCell: (params: any) => {
        return (
          <>
            {!isReadOnly && (
              <>
                <Tooltip title={t('general.action-buttons.edit')}>
                  <Button onClick={() => handleOnEdit(params.id)}>
                    <EditIcon />
                  </Button>
                </Tooltip>
                <Tooltip title={t('general.action-buttons.delete')}>
                  <Button onClick={() => handleOnDelete(params.id)}>
                    <DeleteIcon />
                  </Button>
                </Tooltip>
              </>
            )}
          </>
        );
      },
    },
  ];

  const handleOnCreate = () =>
    navigate(`${paths.ASSETS_DETAIL}/${parentId}${paths.DASHBOARDS_CREATE}`);
  const handleOnEdit = (id: number) =>
    navigate(`${paths.ASSETS_DETAIL}/${parentId}${paths.DASHBOARDS_DETAIL}/${id}`);

  const handleOnDelete = (id: number) => setDeleteAttempt(id);

  const handleConfirm = () => {
    if (!idToDelete) return;

    mutateDeleteDashboard(idToDelete, {
      onSuccess: () => {
        enqueueSnackbar(dictionary.DASHBOARD_DELETED, { variant: 'success' });
        queryClient.invalidateQueries(['dashboards']);
        confirmDeleteAttempt();
      },
      onError: (result: any) => {
        enqueueSnackbar(`${result}`, { variant: 'error' });
      },
    });
  };

  if (isError) return <ApiError />;
  if (isLoading || !dashboardList) return <Loader />;
  return (
    <>
      <Grid item xs={12}>
        <TableContainer component={Paper}>
          <div style={{ height: '65vh', width: '100%' }}>
            <DataGridTable
              height={'65vh'}
              data={dashboardList}
              columns={columns}
              fulltextSearch={true}
              autoPageSize={true}
            />
          </div>
          {!isReadOnly && (
            <Tooltip title={t('general.action-buttons.new')}>
              <Button onClick={handleOnCreate}>
                <AddIcon />
              </Button>
            </Tooltip>
          )}
        </TableContainer>
      </Grid>
      <AlertDialog
        open={isDeleteDialogOpen}
        title={t('general.action-buttons.delete')}
        content={t('general.messages.warning.delete-message')}
        handleClose={cancelDeleteAttempt}
        handleConfirm={handleConfirm}
      />
    </>
  );
}
